import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faArrowRight, faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faFacebookF, faInstagram, faLinkedinIn, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import config from '../config';
import { useLanguage } from '../i18n/LanguageContext';
import './Footer.css';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [newsletterState, setNewsletterState] = useState('idle'); // idle | sending | success | error
  const { t } = useLanguage();

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;

    setNewsletterState('sending');
    try {
      if (config.newsletterWebhook) {
        const res = await fetch(config.newsletterWebhook, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, source: 'ai-dynamics.fr', timestamp: new Date().toISOString() })
        });
        if (!res.ok) throw new Error('Newsletter failed');
      } else {
        await new Promise(r => setTimeout(r, 800));
      }
      setNewsletterState('success');
      setEmail('');
      setTimeout(() => setNewsletterState('idle'), 4000);
    } catch (err) {
      setNewsletterState('error');
      setTimeout(() => setNewsletterState('idle'), 4000);
    }
  };

  return (
    <footer className="footer">
      <div className="footer__main container">
        <div className="footer__brand">
          <div className="footer__logo">
            <img src="/images/logo-ai-dynamics.png" alt="AI Dynamics" className="footer__logo-img" />
          </div>
          <p className="footer__tagline">
            {t('footer.tagline')}
          </p>
          <div className="footer__socials">
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faFacebookF} /></a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faInstagram} /></a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faLinkedinIn} /></a>
            <a href="https://x.com" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faXTwitter} /></a>
          </div>
        </div>

        <div className="footer__links">
          <h4>{t('footer.solutions')}</h4>
          <ul>
            <li><Link to="/services">{t('nav.services')}</Link></li>
            <li><Link to="/niroprod">NIROPROD</Link></li>
            <li><Link to="/techmill">TECHMILL</Link></li>
          </ul>
        </div>

        <div className="footer__links">
          <h4>{t('footer.company')}</h4>
          <ul>
            <li><Link to="/pourquoi-nous">{t('nav.whyUs')}</Link></li>
            <li><Link to="/blog">{t('blog.navLabel')}</Link></li>
            <li><Link to="/contact">{t('nav.contact')}</Link></li>
          </ul>
        </div>

        <div className="footer__newsletter">
          <h4>{t('footer.newsletter')}</h4>
          <p>{t('footer.newsletterDesc')}</p>
          <form className="footer__newsletter-form" onSubmit={handleNewsletter}>
            <div className={`footer__input-wrapper ${newsletterState === 'error' ? 'footer__input-wrapper--error' : ''}`}>
              <FontAwesomeIcon icon={faEnvelope} className="footer__input-icon" />
              <input
                type="email"
                placeholder={t('footer.newsletterPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={newsletterState === 'sending'}
                required
              />
              <button type="submit" disabled={newsletterState === 'sending'} aria-label={t('footer.newsletter')}>
                {newsletterState === 'sending' && <FontAwesomeIcon icon={faSpinner} spin />}
                {newsletterState === 'success' && <FontAwesomeIcon icon={faCheck} />}
                {(newsletterState === 'idle' || newsletterState === 'error') && <FontAwesomeIcon icon={faArrowRight} />}
              </button>
            </div>
          </form>
          {newsletterState === 'success' && (
            <p className="footer__newsletter-msg footer__newsletter-msg--success">{t('footer.newsletterSuccess')}</p>
          )}
          {newsletterState === 'error' && (
            <p className="footer__newsletter-msg footer__newsletter-msg--error">{t('footer.newsletterError')}</p>
          )}
        </div>
      </div>

      <div className="footer__bottom">
        <div className="container footer__bottom-inner">
          <p>&copy; 2026 AI Dynamics. {t('footer.rights')}</p>
          <div className="footer__bottom-links">
            <Link to="/mentions-legales">{t('footer.legal')}</Link>
            <span className="footer__bottom-sep">&middot;</span>
            <Link to="/confidentialite">{t('footer.privacy')}</Link>
            <span className="footer__bottom-sep">&middot;</span>
            <a href="mailto:contact@ai-dynamics.fr">contact@ai-dynamics.fr</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
